import { useEffect, useMemo, useCallback } from "react"
import { DataTable, DataTableBody, DataTableGroupRow, DataTableEmptyState } from "~/react/ui/data-table"
import { DataTableBulkActionsBar } from "~/react/components/data-table-bulk-actions-bar"
import { DataTableSkeleton } from "~/react/components/data-table/data-table-skeleton"
import { Sheet, SheetContent } from "~/react/ui/sheet"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Checkbox } from "~/react/ui/checkbox"
import { Alert, AlertDescription, AlertIcon, AlertContent } from "~/react/ui/alert"
import { email_accounts_mailboxes_path, email_account_path } from "~/lib/routes"
import { useTableSelection } from "~/react/hooks/useTableSelection"
import {
  filterEmailAccounts,
  type EmailAccount,
  type TeamMember,
} from "~/react/components/email_accounts/email_accounts_filters"
import { InboxProtectionModal } from "~/react/components/email_accounts_center/inbox_protection_modal"
import EmailAccountSidepanel from "~/react/components/email_accounts/email_account_sidepanel"
import type {
  BillingInfo,
  CreditCardInfo,
  PricePerWarmup,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"

import { isPendingAccount, resolveAccountStatus } from "./utils"
import { useEmailAccountsData } from "./hooks/use-email-accounts-data"
import { useURLFilters, DEFAULT_FILTERS } from "./hooks/use-url-filters"
import { useColumnOrder } from "./hooks/use-column-order"
import { useBulkEdit } from "./hooks/use-bulk-edit"
import { useBulkActions } from "./hooks/use-bulk-actions"
import { buildDefaultColumns } from "./columns"
import { ConfirmationDialog } from "./components/confirmation-dialog"
import { EmailAccountRow } from "./components/email-account-row"
import { TableHeader } from "./components/table-header"
import { TableToolbar, TableFilterTags } from "./components/table-toolbar"
import { BulkEditSheet } from "./components/bulk-edit-sheet"

type Props = {
  total?: number
  teamMembers?: TeamMember[]
  sharedWorkspace?: boolean
  onOpenConnectSidepanel?: (filter?: "managed" | "connected") => void
  onEmailAccountIdChange?: (id: number | null) => void
  initialEmailAccountId?: number | null
  allowEmailProvisioning?: boolean
  isPremium?: boolean
  isWarmupEnabled?: boolean
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
  maxEmailAccounts: number
}

export default function EmailAccountsTable({
  total,
  teamMembers,
  sharedWorkspace = false,
  onOpenConnectSidepanel,
  onEmailAccountIdChange,
  initialEmailAccountId,
  allowEmailProvisioning = false,
  isPremium = false,
  isWarmupEnabled = false,
  stripePublicKey,
  creditCardInfo,
  cardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
}: Props) {
  // --- Data layer ---
  const {
    data,
    loading,
    error,
    capacityData,
    statusData,
    statusFetchError,
    loadingCapacity,
    fetchData,
    fetchStatuses,
    setData,
    setCapacityData,
    setActiveAccount,
    activeAccount,
  } = useEmailAccountsData(onEmailAccountIdChange)

  // --- Filters ---
  const [filters, setFilters] = useURLFilters()

  // --- Resolve statuses and filter ---
  const dataWithResolvedStatus = useMemo(() => {
    if (!data) return null
    const resolveStatus = (account: EmailAccount): EmailAccount => {
      const status = resolveAccountStatus(account, statusData) as EmailAccount["status"] | undefined
      return { ...account, status }
    }
    return {
      connected: (data.connected ?? []).map(resolveStatus),
      managed: (data.managed ?? []).map(resolveStatus),
      total: data.total,
    }
  }, [data, statusData])

  const filteredData = useMemo(
    () => filterEmailAccounts(dataWithResolvedStatus, filters),
    [dataWithResolvedStatus, filters],
  )

  const transformedData = useMemo(() => {
    if (!filteredData) return null
    const transformAccount = (account: EmailAccount) => ({
      ...account,
      id: account.id ?? (`pending:${account.email}` as string | number),
    })
    return {
      connected: filteredData.connected?.map(transformAccount),
      managed: filteredData.managed?.map(transformAccount),
      total: filteredData.total,
    }
  }, [filteredData])

  // --- Selection ---
  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    clearSelection,
    selectAll,
    allItems,
    connectedItems,
    managedItems,
    isSelected,
  } = useTableSelection(transformedData)

  const getNonPendingItems = useCallback(
    (items: typeof allItems) => items.filter((account) => !isPendingAccount(account)),
    [],
  )

  const nonPendingItems = useMemo(() => getNonPendingItems(allItems), [allItems, getNonPendingItems])
  const isAllNonPendingSelected = useMemo(
    () => nonPendingItems.length > 0 && nonPendingItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingItems, selectedIds],
  )

  const nonPendingConnectedItems = useMemo(
    () => getNonPendingItems(connectedItems),
    [connectedItems, getNonPendingItems],
  )
  const nonPendingManagedItems = useMemo(() => getNonPendingItems(managedItems), [managedItems, getNonPendingItems])

  const isConnectedSectionNonPendingSelected = useMemo(
    () =>
      nonPendingConnectedItems.length > 0 && nonPendingConnectedItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingConnectedItems, selectedIds],
  )
  const isManagedSectionNonPendingSelected = useMemo(
    () => nonPendingManagedItems.length > 0 && nonPendingManagedItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingManagedItems, selectedIds],
  )

  const selectNonPendingSection = useCallback(
    (section: "connected" | "managed") => {
      const sectionItems = section === "connected" ? nonPendingConnectedItems : nonPendingManagedItems
      const sectionIds = sectionItems.map((item) => item.id)
      const allSelected = sectionIds.length > 0 && sectionIds.every((id) => selectedIds.includes(id))

      if (allSelected) {
        const otherSelectedIds = selectedIds.filter((id) => !sectionIds.includes(id))
        const otherSelectedItems = allItems.filter((item) => otherSelectedIds.includes(item.id))
        selectAll(otherSelectedItems)
      } else {
        const currentSelectedIds = new Set(selectedIds)
        sectionIds.forEach((id) => currentSelectedIds.add(id))
        const allSelectedItems = allItems.filter((item) => currentSelectedIds.has(item.id))
        selectAll(allSelectedItems)
      }
    },
    [nonPendingConnectedItems, nonPendingManagedItems, selectedIds, allItems, selectAll],
  )

  // --- Bulk edit ---
  const {
    bulkEditOpen,
    setBulkEditOpen,
    bulkEditLoading,
    bulkEditError,
    bulkEditData,
    setBulkEditData,
    bulkEditAccount,
    handleEdit,
    handleBulkEditSubmit,
    getNumericIds,
  } = useBulkEdit({ allItems, selectedIds, clearSelection, setData })

  // --- Bulk & individual actions ---
  const {
    handleIndividualRunHealthCheck,
    handleIndividualDisconnect,
    handleIndividualCancelRenewal,
    handleIndividualReconnect,
    handleIndividualDelete,
    executeIndividualDisconnect,
    executeIndividualCancelRenewal,
    executeBulkDisconnect,
    executeBulkCancelRenewal,
    bulkActions: rawBulkActions,
    pendingAction,
    confirmBulkDisconnectOpen,
    setConfirmBulkDisconnectOpen,
    confirmBulkCancelRenewalOpen,
    setConfirmBulkCancelRenewalOpen,
    confirmIndividualDisconnectOpen,
    setConfirmIndividualDisconnectOpen,
    confirmIndividualCancelRenewalOpen,
    setConfirmIndividualCancelRenewalOpen,
    inboxProtectionModalOpen,
    setInboxProtectionModalOpen,
    inboxProtectionAccountIds,
  } = useBulkActions({
    allItems,
    selectedIds,
    selectedCount,
    clearSelection,
    fetchData,
    getNumericIds,
    isWarmupEnabled,
    isPremium,
    setData,
    setActiveAccount,
  })

  // Wire up the edit action from bulkActions (the hook can't reference handleEdit directly)
  const bulkActions = useMemo(
    () =>
      rawBulkActions.map((action) =>
        action.label === i18n.t("js.email_accounts_center.bulk.edit") ? { ...action, onClick: handleEdit } : action,
      ),
    [rawBulkActions, handleEdit],
  )

  // --- Open account sidepanel ---
  const openAccount = useCallback(
    (account: EmailAccount) => {
      App.analytics.posthog_capture("Email account center: Email account sidepanel open", {
        email_account_id: account.id,
      })
      setActiveAccount(account)
      onEmailAccountIdChange?.(account.id ?? null)
    },
    [onEmailAccountIdChange, setActiveAccount],
  )

  // Open sidepanel for initialEmailAccountId when data has loaded
  useEffect(() => {
    if (!data || initialEmailAccountId == null) return

    const allAccounts = [...(data.connected || []), ...(data.managed || [])].filter(
      (a): a is EmailAccount & { id: number } => a.id != null,
    )
    if (allAccounts.length === 0) return

    const account =
      initialEmailAccountId === -1
        ? allAccounts.reduce((latest, a) => (a.id > latest.id ? a : latest))
        : allAccounts.find((a) => a.id === initialEmailAccountId)

    if (account) openAccount(account)
  }, [data, initialEmailAccountId, openAccount])

  // --- Columns ---
  const defaultColumns = useMemo(
    () =>
      buildDefaultColumns(
        { sharedWorkspace, isPremium, isWarmupEnabled, capacityData, statusData, loadingCapacity },
        {
          onViewAccount: openAccount,
          onRunHealthCheck: handleIndividualRunHealthCheck,
          onDisconnect: handleIndividualDisconnect,
          onCancelRenewal: handleIndividualCancelRenewal,
          onReconnect: handleIndividualReconnect,
          onDelete: handleIndividualDelete,
        },
      ),
    [
      sharedWorkspace,
      isPremium,
      isWarmupEnabled,
      capacityData,
      statusData,
      loadingCapacity,
      openAccount,
      handleIndividualRunHealthCheck,
      handleIndividualDisconnect,
      handleIndividualCancelRenewal,
      handleIndividualReconnect,
      handleIndividualDelete,
    ],
  )

  const { columns, draggingColumnId, dragHandlers } = useColumnOrder(defaultColumns)

  // --- Skeleton columns for loading state ---
  const skeletonColumns = columns
    .filter((column) => column.id !== "actions")
    .map((column) => ({ label: column.renderHeader(), sticky: column.sticky }))

  // --- Counts ---
  const totalCount = filteredData?.total ?? total ?? 0
  const connectedCount = connectedItems.length
  const managedCount = managedItems.length
  const connectedActiveCount = connectedItems.filter((a) => resolveAccountStatus(a, statusData) === "active").length
  const managedActiveCount = managedItems.filter((a) => resolveAccountStatus(a, statusData) === "active").length

  const hasActiveFilters =
    filters.searchQuery ||
    filters.status.length > 0 ||
    filters.healthFrom !== null ||
    filters.healthTo !== null ||
    filters.healthIsEmpty ||
    filters.healthHasAnyValue ||
    filters.ownerIds.length > 0

  // --- Loading state ---
  if (loading) {
    return (
      <div>
        <TableToolbar
          totalCount={0}
          filters={filters}
          setFilters={setFilters}
          teamMembers={teamMembers}
          sharedWorkspace={sharedWorkspace}
          loading
        />
        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.bulk.select_all")}
        />
      </div>
    )
  }

  // --- Error state ---
  if (error) {
    return (
      <div>
        <TableToolbar
          totalCount={0}
          filters={filters}
          setFilters={setFilters}
          teamMembers={teamMembers}
          sharedWorkspace={sharedWorkspace}
          error={error}
        />
        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.bulk.select_all")}
        />
      </div>
    )
  }

  // --- Account update handler for sidepanel ---
  const handleAccountUpdated = async (updatedAccount: EmailAccount) => {
    // Immediately update activeAccount and table data if health or health_check_status changed
    const isHealthUpdate = updatedAccount.health !== undefined || updatedAccount.health_check_status !== undefined

    if (isHealthUpdate) {
      // Extract domain from updated account
      const accountDomain = updatedAccount.email.split("@")[1]?.toLowerCase()

      setActiveAccount((prev) => {
        if (!prev || prev.id !== updatedAccount.id) return prev
        return {
          ...prev,
          ...(updatedAccount.health !== undefined && {
            health: updatedAccount.health,
          }),
          ...(updatedAccount.health_check_status !== undefined && {
            health_check_status: updatedAccount.health_check_status,
          }),
        }
      })

      // Update all accounts with the same domain in the table data immediately
      setData((prev) => {
        if (!prev) return prev
        const updateAccount = (accounts: EmailAccount[] | undefined) =>
          accounts?.map((acc) => {
            const accDomain = acc.email.split("@")[1]?.toLowerCase()
            if (accDomain === accountDomain) {
              return {
                ...acc,
                ...(updatedAccount.health !== undefined && {
                  health: updatedAccount.health,
                }),
                ...(updatedAccount.health_check_status !== undefined && {
                  health_check_status: updatedAccount.health_check_status,
                }),
              }
            }
            return acc
          })
        return {
          ...prev,
          connected: updateAccount(prev.connected) ?? prev.connected,
          managed: updateAccount(prev.managed) ?? prev.managed,
        }
      })

      // For health check updates, skip the full refresh since ActionCable already handles it
      // Just update activeAccount with the full updated account
      setActiveAccount(updatedAccount)
      return
    }

    // Fetch the show endpoint to get fresh capacity data (sent_in_current_daily_limit, limit, pending_messages)
    try {
      const response = await fetch(email_account_path(updatedAccount.id!, { format: "json" }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (response.ok) {
        const showData = await response.json()
        setCapacityData((prev) => ({
          ...prev,
          [updatedAccount.id!]: {
            sent: showData.sent_in_current_daily_limit ?? 0,
            limit: showData.limit ?? 0,
            scheduled: showData.pending_messages ?? 0,
          },
        }))

        if (showData.display_name !== undefined) {
          setData((prev) => {
            if (!prev) return prev
            const updateAccount = (account: EmailAccount) =>
              account.id === updatedAccount.id ? { ...account, display_name: showData.display_name } : account
            return {
              ...prev,
              connected: prev.connected?.map(updateAccount),
              managed: prev.managed?.map(updateAccount),
            }
          })
        }
      }
    } catch (err) {
      console.error(`Failed to fetch show data for account ${updatedAccount.id}:`, err)
    }

    const freshData = await fetchData(false)
    if (freshData) {
      const allAccounts = [...(freshData.connected || []), ...(freshData.managed || [])]
      const freshAccount = allAccounts.find((acc) => acc.id === updatedAccount.id)
      setActiveAccount(freshAccount ?? updatedAccount)
    } else {
      setActiveAccount(updatedAccount)
    }
  }

  return (
    <div>
      <TableToolbar
        totalCount={totalCount}
        filters={filters}
        setFilters={setFilters}
        teamMembers={teamMembers}
        sharedWorkspace={sharedWorkspace}
      />

      <TableFilterTags
        filters={filters}
        setFilters={setFilters}
        teamMembers={teamMembers}
        sharedWorkspace={sharedWorkspace}
      />

      {statusFetchError && (
        <Alert variant="warning" className="mb-4">
          <AlertIcon />
          <AlertContent>
            <AlertDescription>
              {i18n.t("js.email_accounts_center.errors.status_fetch_failed")}{" "}
              <Button variant="link" size="sm" className="p-0 h-auto min-h-0" onClick={() => fetchStatuses()}>
                {i18n.t("js.email_accounts_center.errors.status_fetch_retry")}
              </Button>
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      <DataTable
        bulkActionsBar={
          <DataTableBulkActionsBar
            totalCount={totalCount}
            pageCount={allItems.length}
            selectedCount={selectedCount}
            onClearSelection={clearSelection}
            onSelectAll={() => selectAll(getNonPendingItems(allItems))}
            onSelectPage={() => selectAll(getNonPendingItems(allItems))}
            actions={bulkActions}
          />
        }
      >
        <TableHeader
          columns={columns}
          draggingColumnId={draggingColumnId}
          isAllSelected={isAllNonPendingSelected}
          onToggleSelectAll={() => selectAll(isAllNonPendingSelected ? [] : getNonPendingItems(allItems))}
          connectedCount={connectedCount}
          managedCount={managedCount}
          connectedActiveCount={connectedActiveCount}
          managedActiveCount={managedActiveCount}
          dragHandlers={dragHandlers}
        />

        <DataTableBody>
          {totalCount === 0 && (
            <DataTableEmptyState
              colSpan={columns.length + 1}
              onClearFilters={hasActiveFilters ? () => setFilters(DEFAULT_FILTERS) : undefined}
            />
          )}

          {connectedCount > 0 && (
            <>
              {managedCount > 0 && (
                <DataTableGroupRow
                  colSpan={columns.length + 1}
                  sectionId="connected"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.groups.select_connected_group_aria")}
                      checked={isConnectedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("connected")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary">
                    {i18n.t("js.email_accounts_center.groups.connected_title")}
                  </strong>
                  <span className="text-xs text-hint">
                    · {connectedActiveCount}/{connectedCount}
                  </span>
                  <Button size="xs" className="ml-auto" onClick={() => onOpenConnectSidepanel?.("connected")}>
                    <Icon icon="plus" size="sm" aria-hidden="true" />
                    {i18n.t("js.email_accounts_center.groups.connect_email_account")}
                  </Button>
                </DataTableGroupRow>
              )}

              {connectedItems.map((account) => (
                <EmailAccountRow
                  key={account.id ?? `pending:${account.email}`}
                  account={account}
                  section="connected"
                  columns={columns}
                  statusData={statusData}
                  isSelected={isSelected(account.id ?? `pending:${account.email}`)}
                  onToggleSelection={() => toggleSelection(account.id ?? `pending:${account.email}`)}
                />
              ))}
            </>
          )}

          {managedCount > 0 && (
            <>
              {connectedCount > 0 && (
                <DataTableGroupRow
                  colSpan={columns.length + 1}
                  sectionId="managed"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.groups.select_group_aria")}
                      checked={isManagedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("managed")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary mr-1">
                    {i18n.t("js.email_accounts_center.groups.managed_title")}
                  </strong>
                  <span className="text-xs text-hint ml-1">
                    · {managedActiveCount}/{managedCount}
                  </span>
                  {allowEmailProvisioning && (
                    <Button size="xs" className="ml-auto" type="button" asChild>
                      <a href={email_accounts_mailboxes_path()} data-turbo="false">
                        <Icon icon="plus" className="mr-1" aria-hidden="true" />
                        {i18n.t("js.email_accounts_center.groups.create_email_accounts")}
                      </a>
                    </Button>
                  )}
                </DataTableGroupRow>
              )}

              {managedItems.map((account) => (
                <EmailAccountRow
                  key={account.id ?? `pending:${account.email}`}
                  account={account}
                  section="managed"
                  columns={columns}
                  statusData={statusData}
                  isSelected={isSelected(account.id ?? `pending:${account.email}`)}
                  onToggleSelection={() => toggleSelection(account.id ?? `pending:${account.email}`)}
                />
              ))}
            </>
          )}
        </DataTableBody>
      </DataTable>

      {/* Individual Account Sidepanel */}
      <Sheet
        open={!!activeAccount}
        modal={false}
        onOpenChange={(open) => {
          const hasOpenModal = document.querySelector(".modal.show")
          if (!open && !hasOpenModal) {
            setActiveAccount(null)
            onEmailAccountIdChange?.(null)
          }
        }}
      >
        <SheetContent
          side="right"
          size="md"
          className="p-0"
          onEscapeKeyDown={(e) => {
            const hasOpenModal = document.querySelector(".modal.show")
            if (hasOpenModal) e.preventDefault()
          }}
        >
          {activeAccount && (
            <EmailAccountSidepanel
              account={activeAccount}
              statusData={statusData}
              isPremium={isPremium}
              isWarmupEnabled={isWarmupEnabled}
              stripePublicKey={stripePublicKey}
              creditCardInfo={creditCardInfo}
              cardholderName={cardholderName}
              billingInfo={billingInfo}
              userEmail={userEmail}
              pricePerWarmup={pricePerWarmup}
              onAccountUpdated={handleAccountUpdated}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Edit Sheet */}
      <BulkEditSheet
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        count={bulkEditAccount._bulkEdit?.accountIds.length ?? 0}
        error={bulkEditError}
        loading={bulkEditLoading}
        bulkEditAccount={bulkEditAccount}
        formData={bulkEditData}
        setFormData={setBulkEditData}
        onSubmit={handleBulkEditSubmit}
      />

      <InboxProtectionModal
        isOpen={isWarmupEnabled && inboxProtectionModalOpen}
        onClose={() => setInboxProtectionModalOpen(false)}
        onLearnMore={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
        emailAccountIDs={inboxProtectionAccountIds}
        stripePublicKey={stripePublicKey}
        creditCardInfo={creditCardInfo}
        cardholderName={cardholderName}
        billingInfo={billingInfo}
        userEmail={userEmail}
        pricePerWarmup={pricePerWarmup}
      />

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={confirmBulkDisconnectOpen}
        onClose={() => setConfirmBulkDisconnectOpen(false)}
        onConfirm={executeBulkDisconnect}
        title={i18n.t("js.email_accounts_center.actions.disconnect")}
        description={i18n.t("js.email_accounts_center.bulk.disconnect_confirm", { count: selectedIds.length })}
        confirmLabel={i18n.t("js.email_accounts_center.actions.disconnect")}
      />

      <ConfirmationDialog
        open={confirmBulkCancelRenewalOpen}
        onClose={() => setConfirmBulkCancelRenewalOpen(false)}
        onConfirm={executeBulkCancelRenewal}
        title={i18n.t("js.email_accounts_center.actions.cancel_renewal")}
        description={i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", { count: selectedIds.length })}
        confirmLabel={i18n.t("js.email_accounts_center.actions.cancel_renewal")}
      />

      <ConfirmationDialog
        open={confirmIndividualDisconnectOpen && !!pendingAction?.account}
        onClose={() => setConfirmIndividualDisconnectOpen(false)}
        onConfirm={() =>
          pendingAction?.account ? executeIndividualDisconnect(pendingAction.account) : Promise.resolve()
        }
        title={i18n.t("js.email_accounts_center.actions.disconnect")}
        description={i18n.t("js.email_accounts_center.bulk.disconnect_confirm", { count: 1 })}
        confirmLabel={i18n.t("js.email_accounts_center.actions.disconnect")}
      />

      <ConfirmationDialog
        open={confirmIndividualCancelRenewalOpen && !!pendingAction?.account}
        onClose={() => setConfirmIndividualCancelRenewalOpen(false)}
        onConfirm={() =>
          pendingAction?.account ? executeIndividualCancelRenewal(pendingAction.account) : Promise.resolve()
        }
        title={i18n.t("js.email_accounts_center.actions.cancel_renewal")}
        description={i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", { count: 1 })}
        confirmLabel={i18n.t("js.email_accounts_center.actions.cancel_renewal")}
      />
    </div>
  )
}
